/**
 * Curated device brand → model catalog for the "New device" picker.
 *
 * The API stores `brand` / `model` as free text (no enumerated vocabulary
 * exists server-side), so this list is a frontend product decision, not a
 * mirror of a backend type. It is intentionally a flat, hand-maintained
 * catalog skewed to the entry-level Android handsets common in the
 * Tanzanian market — extend it as the financed-device range grows.
 *
 * Order matters only for display: brands and models render in array order.
 */
export interface BrandCatalogEntry {
  readonly brand: string;
  readonly models: readonly string[];
}

export const DEVICE_CATALOG: readonly BrandCatalogEntry[] = [
  {
    brand: "Tecno",
    models: ["Spark 20", "Spark 20 Pro", "Camon 20", "Camon 30", "Pop 8", "Pova 6"],
  },
  {
    brand: "Infinix",
    models: ["Smart 8", "Hot 40", "Hot 40 Pro", "Note 40", "Zero 30", "GT 20 Pro"],
  },
  {
    brand: "itel",
    models: ["A70", "A60s", "S23", "S24", "P55", "Vision 3"],
  },
  {
    brand: "Samsung",
    models: ["Galaxy A05", "Galaxy A06", "Galaxy A15", "Galaxy A25", "Galaxy A35", "Galaxy M15"],
  },
  {
    brand: "Xiaomi",
    models: ["Redmi A3", "Redmi 13C", "Redmi Note 13", "Redmi Note 13 Pro", "Poco C65", "Poco M6"],
  },
  {
    brand: "Nokia",
    models: ["C32", "G42", "G22", "105", "110"],
  },
  {
    brand: "Oppo",
    models: ["A18", "A38", "A60", "A79", "Reno 11"],
  },
  {
    brand: "vivo",
    models: ["Y03", "Y18", "Y28", "Y36", "V30"],
  },
] as const;

/** Models for a brand, or an empty list when the brand is unknown/blank. */
export function modelsForBrand(brand: string): readonly string[] {
  return DEVICE_CATALOG.find((b) => b.brand === brand)?.models ?? [];
}

export interface ControlProviderOption {
  /** Persisted as device.controlProvider (free text server-side). */
  readonly value: string;
  readonly label: string;
}

/**
 * Lock-control adapters the platform can enrol a device against. The first
 * three mirror the real provider packages (`@dfp/provider-knox`,
 * `provider-android-do`, `provider-ios-mdm`); `mock` is the no-op adapter
 * used in dev/test environments without a live MDM.
 */
export const CONTROL_PROVIDERS: readonly ControlProviderOption[] = [
  { value: "knox", label: "Samsung Knox" },
  { value: "android-do", label: "Android Device Owner" },
  { value: "ios-mdm", label: "Apple iOS MDM" },
  { value: "mock", label: "Mock (test)" },
] as const;
